/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable jsdoc/require-jsdoc */
import React, { ReactElement, useState } from 'react';
import { DndContext, closestCenter, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, rectSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { toast } from 'react-toastify';
import { ParameterAttribute, ParametersType } from '../constants/telosair-interfaces';
import { useData } from '../context/data.context';

import { TOAST_MESSAGES } from '../constants/global-constants';
import { updateParameterSequence } from '../services/dashboard-service';
import Loader from './loader';

interface DraggableItemProps {
  id: ParametersType;
  name: string;
  index: number;
  enabled: boolean;
  onToggle: (id: ParametersType) => void;
}

function DraggableItem({ id, index, name, enabled, onToggle }: DraggableItemProps): ReactElement {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  };

  return (
    <div
      className={`bg-container-800 border border-gray-900 rounded p-4 flex items-center justify-between gap-2 ${
        enabled ? 'text-gray-300' : 'text-gray-500'
      }`}
      ref={setNodeRef}
      style={style}
    >
      {/* Only the handle starts a drag: the rest of the row has to stay
          clickable for the show/hide toggle. */}
      <span className="flex items-center min-w-0">
        <span className="mx-1 material-icons-outlined cursor-grab" {...attributes} {...listeners}>
          drag_handle
        </span>
        <span className={`truncate ${enabled ? '' : 'line-through'}`}>
          {index}. {name}
        </span>
      </span>
      <button
        type="button"
        onClick={() => onToggle(id)}
        title={enabled ? `Hide ${name}` : `Show ${name}`}
        aria-label={enabled ? `Hide ${name}` : `Show ${name}`}
        aria-pressed={enabled}
        className="material-icons-outlined shrink-0 text-xl leading-none cursor-pointer hover:text-white"
      >
        {enabled ? 'visibility' : 'visibility_off'}
      </button>
    </div>
  );
}

interface DraggableListProps {
  items: ParameterAttribute[];
  setLoading: (value: boolean) => void;
  onChange: (items: ParameterAttribute[]) => void;
  onSaved: () => Promise<void>;
}

function DraggableList({ items, onChange, setLoading, onSaved }: DraggableListProps): ReactElement {
  /**
   * Persists the parameter list (order and visibility) for the client.
   *
   * A client that is still on the shared default parameters gets its own copies
   * written on the first save, which changes every row's id. Re-reading the list
   * afterwards keeps the ids in memory valid, so a second edit updates those
   * rows instead of trying to clone the defaults again.
   * @param parametersData - Parameter data.
   * @returns Promise<void> A Promise that resolves when the data is saved.
   */
  const saveParameters = async (parametersData: ParameterAttribute[]): Promise<void> => {
    try {
      setLoading(true);
      await updateParameterSequence(parametersData);
      await onSaved();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
      toast.error(TOAST_MESSAGES.error);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line jsdoc/require-jsdoc
  const handleDragEnd = async (event: DragEndEvent): Promise<void> => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = items.findIndex((item) => item.parameterName === active.id);
      const newIndex = items.findIndex((item) => item.parameterName === over.id);

      const newItems = arrayMove(items, oldIndex, newIndex).map((parameter, index) => ({
        ...parameter,
        order: index + 1
      }));
      onChange(newItems);
      await saveParameters(newItems);
    }
  };

  /**
   * Shows or hides a parameter across the dashboard.
   * @param id - Parameter to toggle.
   */
  const handleToggle = async (id: ParametersType): Promise<void> => {
    const newItems = items.map((parameter) =>
      parameter.parameterName === id
        ? { ...parameter, isEnabled: parameter.isEnabled === false }
        : parameter
    );
    onChange(newItems);
    await saveParameters(newItems);
  };

  const PARAMETER_HALF_INDEX = Math.round(items.length / 2);

  // eslint-disable-next-line jsdoc/require-jsdoc
  const renderItem = (item: ParameterAttribute, index: number): ReactElement => (
    <DraggableItem
      key={item.parameterName}
      index={index}
      id={item.parameterName}
      name={item.shortName}
      enabled={item.isEnabled !== false}
      onToggle={handleToggle}
    />
  );

  return (
    <DndContext onDragEnd={handleDragEnd} collisionDetection={closestCenter}>
      <SortableContext
        items={items.map((item) => ({ id: item.parameterName }))}
        strategy={rectSortingStrategy}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-4">
            {items
              .slice(0, PARAMETER_HALF_INDEX)
              .map((item, index) => renderItem(item, index + 1))}
          </div>
          <div className="space-y-4">
            {items
              .slice(PARAMETER_HALF_INDEX)
              .map((item, index) => renderItem(item, index + PARAMETER_HALF_INDEX + 1))}
          </div>
        </div>
      </SortableContext>
    </DndContext>
  );
}

/**
 * Component to edit the parameter layout: their order, and whether each one is
 * shown at all. Parameters none of the client's sensors report can be switched
 * off here so they stop appearing as empty series on plots.
 @returns - React component.
 */
export default function SequenceEditor(): ReactElement {
  const { allParameters, setParameters, fetchParameterList } = useData();

  const [loading, setLoading] = useState(false);

  const hiddenCount = allParameters.filter((item) => item.isEnabled === false).length;

  return (
    <div className="p-6 my-4 text-white text-base bg-container-500 flex flex-1 flex-col justify-between rounded relative">
      {loading && <Loader />}
      <div className="flex flex-col gap-6">
        <div>
          <h1 className="text-2xl font-bold">Layout</h1>
          <p className="text-sm text-gray-400 mt-1">
            Drag to reorder. Use the eye icon to hide a parameter your sensors don&apos;t
            measure — hidden parameters are left out of every chart, card and export.
            {hiddenCount > 0 ? ` ${hiddenCount} hidden.` : ''}
          </p>
        </div>
        <DraggableList
          setLoading={setLoading}
          items={allParameters}
          onChange={setParameters}
          onSaved={fetchParameterList}
        />
      </div>
    </div>
  );
}
